import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { GraduationCap, Users, Clock, CheckCircle2, ClipboardList, MapPin, ShieldCheck, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const AdminTabInternship = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/internship/overview`);
        setData(res.data || {});
      } catch (e) { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/internship/export.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'internship_portal_stats.csv';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not export stats.'); }
  };

  if (loading) return <div className="py-16 text-center text-gray-500" data-testid="admin-internship-loading">Loading…</div>;
  if (!data) return <div className="py-16 text-center text-gray-500">Could not load internship stats.</div>;

  return (
    <div className="space-y-6" data-testid="admin-internship">
      <div className="flex items-start gap-3">
        <GraduationCap className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-serif text-xl font-bold text-primary">Internship Portal Usage</h3>
          <p className="text-gray-500 text-sm">Aggregate, privacy-safe counts only. Individual member logs, pathways, and records are never shown here.</p>
        </div>
        <Button size="sm" variant="outline" className="ml-auto border-primary text-primary" onClick={exportCsv} data-testid="admin-internship-export-csv"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card icon={ShieldCheck} label="Pathways Set" value={data.total_pathways} />
        <Card icon={Users} label="Members Logging Hours" value={data.members_logging} />
        <Card icon={ClipboardList} label="Hour Logs" value={`${data.completed_logs}/${data.total_logs}`} sub="completed / total" />
        <Card icon={Clock} label="Total Hours Logged" value={data.total_hours_logged} />
        <Card icon={ClipboardList} label="Active Trackers" value={data.total_trackers} />
        <Card icon={ClipboardList} label="Applications Tracked" value={data.total_applications} />
        <Card icon={CheckCircle2} label="Completed Logs" value={data.completed_logs} />
        <Card icon={MapPin} label="States With Rules" value={(data.catalog_states || []).length} />
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <Breakdown title="By Pathway Type" map={data.by_pathway_type} labels={{ academic: 'Academic', postgrad: 'Post-Graduate' }} />
        <Breakdown title="By Target License" map={data.by_license} />
        <Breakdown title="By State" map={data.by_state} />
      </div>

      <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 text-sm text-gray-600" data-testid="admin-internship-states">
        <strong className="text-primary">Official requirements loaded for:</strong>{' '}
        {(data.catalog_states || []).join(', ') || 'None yet'}.
      </div>
    </div>
  );
};

const Card = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white rounded-2xl border p-5" data-testid={`admin-internship-stat-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <Icon className="w-5 h-5 text-secondary mb-2" />
    <p className="text-2xl font-bold text-primary">{value ?? 0}</p>
    <p className="text-xs text-gray-500">{label}{sub ? <span className="block">{sub}</span> : null}</p>
  </div>
);

const Breakdown = ({ title, map, labels }) => {
  const entries = Object.entries(map || {});
  return (
    <div className="bg-white rounded-2xl border p-5">
      <h4 className="font-semibold text-primary mb-3">{title}</h4>
      {entries.length === 0 ? (
        <p className="text-sm text-gray-400">No data yet.</p>
      ) : (
        <ul className="space-y-2">
          {entries.map(([k, v]) => (
            <li key={k} className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{(labels && labels[k]) || k}</span>
              <span className="font-semibold text-primary">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminTabInternship;
